import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  // Dubai scroll-up mode preserves the native sticky layout and menu components.
  #scrollReveal = false;
  #scrollFrame = 0;
  #directionAnchor = 0;
  #scrollSign = 0;
  #hideAfter = 0;
  #interactionObserver = null;

  #scrollPosition = () => {
    const root = document.scrollingElement;
    return Math.max(0, Math.min(window.scrollY, (root?.scrollHeight ?? 0) - window.innerHeight));
  };

  #hasOpenNavigation = () => Boolean(this.querySelector('details[open], [aria-expanded="true"]'));

  #revealHeader = () => {
    const y = this.#scrollPosition();
    this.#lastScrollTop = this.#directionAnchor = y;
    this.#scrollSign = 0;
    this.dataset.stickyState = y <= this.#hideAfter ? 'inactive' : 'active';
    this.dataset.scrollDirection = 'up';
  };

  #updateScrollReveal = () => {
    this.#scrollFrame = 0;
    const y = this.#scrollPosition();
    const delta = y - this.#lastScrollTop;

    if (y <= this.#hideAfter || window.Shopify?.designMode || this.#hasOpenNavigation()
      || this.querySelector(':focus-visible') || document.querySelector('dialog[open]')
      || document.documentElement.hasAttribute('scroll-lock')) {
      this.#revealHeader();
      return;
    }

    if (!delta) return;
    const sign = Math.sign(delta);
    if (sign !== this.#scrollSign) {
      this.#directionAnchor = this.#lastScrollTop;
      this.#scrollSign = sign;
    }
    this.#lastScrollTop = y;

    // Accumulated travel rejects trackpad noise; upward intent responds sooner.
    if (Math.abs(y - this.#directionAnchor) < (sign > 0 ? 16 : 8)) return;
    this.dataset.scrollDirection = sign > 0 ? 'down' : 'up';
    this.dataset.stickyState = sign > 0 ? 'idle' : 'active';
  };

  #queueScrollReveal = () => {
    if (!this.#scrollFrame) this.#scrollFrame = requestAnimationFrame(this.#updateScrollReveal);
  };

  #resetScrollReveal = () => {
    // Match the reference's quiet zone: roughly two header heights near the top.
    this.#hideAfter = Math.max(160, this.offsetHeight * 2);
    this.#revealHeader();
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
    if (this.#scrollReveal) this.#resetScrollReveal();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollReveal) {
      this.#queueScrollReveal();
      return;
    }
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    this.#scrollReveal = stickyMode === 'scroll-up' && document.body.classList.contains('rdb-theme');
    if (stickyMode) {
      if (this.#scrollReveal) {
        this.#resetScrollReveal();
        this.addEventListener('focusin', this.#revealHeader);
        window.addEventListener('pageshow', this.#resetScrollReveal);
        this.#interactionObserver = new MutationObserver(() => {
          if (this.#hasOpenNavigation()) this.#revealHeader();
        });
        this.#interactionObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded'] });
      } else {
        this.#observeStickyPosition(stickyMode === 'always');
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#interactionObserver?.disconnect();
    this.#interactionObserver = null;
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = 0;
    if (this.#timeout) clearTimeout(this.#timeout);
    this.removeEventListener('focusin', this.#revealHeader);
    window.removeEventListener('pageshow', this.#resetScrollReveal);
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
